import React, { useState } from 'react';
import BrowserStorage from '../../utils/browser-storage';
import Util from '../../utils/util';
import useChatDatabase, { AuthorList, DBMessage, ChatMessage } from './use-chat-database';
import useChatFixedMobileLayout from './use-chat-fixed-mobile-layout';
import useLocalStorage from '../../hooks/use-local-storage';
import useWindowSize from '../../hooks/use-window-size';
import ChatMessages from './chat-messages';
import ChatEntry from './chat-entry';

export default function Chat(props: {
  broadcast: any;
  view: any;
  fixedMobileLayout: boolean;
  onShowChatToggle: (visible: boolean) => void;
}) {
  const viewerID: string =
    BrowserStorage.getItem('boxcast-viewer-id', null) ||
    BrowserStorage.setItem('boxcast-viewer-id', Util.uuid().replace(/-/g, ''));
  const dbName: string = props.view.settings?.chat_db;

  // Choosing to persist the hide/show chat setting is important to decide whether or not we want to
  // remember the chat state for the next time the user comes back.  It varies based on whether
  // the user is watching *live* or *archived* broadcasts, as well as if they are on mobile.
  //
  //  * Mobile users will always default to hidden chat because the chat experience
  //    can be overwhelming.
  //  * Browser users will default to hidden for *archived* broadcasts because they
  //    can't add more chats after the fact (based on current rules as of this writing)
  const isLiveOrUpcoming = props.broadcast.timeframe !== 'past';
  const { isMobilePortrait } = useWindowSize();
  const shouldUsePersistentSettings = isLiveOrUpcoming && !isMobilePortrait;
  const [showChat, setShowChat] = shouldUsePersistentSettings
    ? // Use localStorage for persisting preference between refresh for live & upcoming events
      useLocalStorage<boolean>('bxc:chat:viz', true)
    : // Default to always hidden for past broadcasts (but let someone open it if they really want to)
      useState<boolean>(false);

  const onShowChatToggle = (newVal: boolean) => {
    setShowChat(newVal);
    props.onShowChatToggle(newVal);
  };

  // Conditionally handle fixed mobile layout overrides (scroll locking, special CSS attribute)
  if (props.fixedMobileLayout) {
    useChatFixedMobileLayout({
      chatVisible: showChat,
      isMobilePortrait,
      broadcast: props.broadcast,
    });
  }

  return (
    <div className="boxcast-chat">
      <div className={`boxcast-chat--shell boxcast-well ${showChat ? '' : 'closed'}`}>
        <div className="boxcast-well-title">
          <h3>Chat</h3>
          <span
            className="boxcast-chat--link"
            onClick={() => {
              onShowChatToggle(!showChat);
            }}
          >
            {/* TODO: Add chevron up icon to font */}
            <span className="boxcast-icon-chevron-up" />
            {showChat ? 'Hide' : 'Show'} Chat
          </span>
        </div>
        {showChat ? (
          <ChatArea
            viewerID={viewerID}
            dbName={dbName}
            broadcast={props.broadcast}
            showChatInput={isLiveOrUpcoming}
            onLeaveChat={() => onShowChatToggle(false)}
          />
        ) : null}
      </div>
    </div>
  );
}

function ChatArea(props: {
  viewerID: string;
  dbName: string;
  broadcast: any;
  showChatInput: boolean;
  onLeaveChat: () => void;
}) {
  const [messages, setMessages] = useState<DBMessage[]>([]);
  const [authorList, setAuthorList] = useState<AuthorList>({});
  const [database, sendMessage] = useChatDatabase({
    dbName: props.dbName,
    broadcast: props.broadcast,
    onMessageFromFirebase: (newMessages) => setMessages(newMessages),
    onAuthorListUpdated: (newAuthorList) => setAuthorList(newAuthorList),
  });

  return (
    <div>
      <ChatMessages
        authorList={authorList}
        hasDatabase={database.current !== null}
        messages={messages}
        showChatInput={props.showChatInput}
        viewerID={props.viewerID}
        emptyState={<ChatEmptyState inputAllowed={props.showChatInput} />}
      />
      {props.showChatInput && (
        <ChatEntry
          onLeaveChat={props.onLeaveChat}
          sendMessage={sendMessage}
          viewerID={props.viewerID}
        />
      )}
    </div>
  );
}

function ChatEmptyState(props: { inputAllowed }) {
  return (
    <div className="boxcast-chat--empty">
      {props.inputAllowed
        ? "No one's talking yet -- start the chat below!"
        : 'This chat session has ended.'}
    </div>
  );
}
